import type { ApiResponse } from '@shared/types'
import type { Edge, Node } from '@xyflow/react'
import { getOutgoers } from '@xyflow/react'
import { produce } from 'immer'
import { cloneDeep, isEqual, maxBy } from 'lodash'
import { queryClient } from 'v2/configs/rq-setup'
import type { RProviders } from 'v2/pages/flow/sidebar/types'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import {
  AgentChannels,
  FlowSourceTypes,
  FlowSourceTypeToTriggerTitle,
  FlowTypes,
  type PhloType,
  type TriggerType,
} from 'v2/types'
import type { RExternalApp, RExternalAppDetails, RFlows } from 'v2/types/api/settings'
import { agentEventBus } from 'v2/utils/event-bus/agent'
import { logger } from 'v2/utils/logger'
import { z } from 'zod'
import { getPayloadPaths, unescapeDotsInKey } from '../components/title-bar/utils'
import type { AgentSettingsFormValues } from '../config/base.config'
import { defaultNodeData, MenuNodeID } from '../config/nodes.config'
import { getDefaultNodeVars } from '../config/nodevars.config'
import { NODE_TYPES_WITH_CHANNEL } from '../constants'
import type { useTextToSpeechMutation } from '../queries'
import { useFlowStore } from '../store'
import type { VoicePreviewParams } from '../types'
import {
  type Action,
  AgentState,
  type EdgeData,
  type FlowData,
  FlowNodeTypes,
  type GenericNode,
  type NodeDataBase,
  SYSTEM_PROMPT_NODE_ID,
  type SystemPrompt,
} from '../types'
import { AGENT_BUILDER_BASE_PATH } from './agent-builder-route'
import { isAiAgentV2Type } from './ai-agent-v29'
import { transformFlowData } from './data-transformer'
import { MultipleNodeValidationError } from './flow-validation'
import { getOutputStates } from './output-states'
import { compareWithIgnoredFields } from './sidebar-form-filter-fields'

export { syncNodeIdsAfterSave } from './sync-node-ids'

export const getMenuNodeEdge = (
  ids: { nodeId: string | null; handleId: string | null },
  target: string,
): Edge => ({
  id: MenuNodeID,
  source: ids.nodeId || '',
  sourceHandle: ids.handleId,
  target,
  style: { strokeWidth: 2, strokeDasharray: 4 },
})

// Feature: Nodevars
const formatNodeVars = (nodeName: string, vars: string[], handleName?: string) => {
  // Filter out empty variables first
  const nonEmptyVars = vars.filter(Boolean)
  return nonEmptyVars.map((variable) => {
    const nameString = `{{${nodeName}${handleName ? `.${handleName}` : ''}.${variable}}}`
    return nameString
  })
}

// Feature: Nodevars - Get all node vars from a node (across all output handles)
export const getAllNodeVarsFromNode = (node?: Node<NodeDataBase<unknown>>) => {
  if (!node) return []

  const type = node.type as FlowNodeTypes
  const outputStates = getOutputStates({ data: node.data.config, type })

  // Collect node vars from all output handles
  const allVars: string[] = []

  // Get default node vars (no handle)
  allVars.push(...getNodeVarsForHandle(node))

  // Special handling for start node - check triggers instead of output states
  if (type === FlowNodeTypes.start) {
    const triggers = (node.data.config as { triggers?: string[] }).triggers || []
    triggers.forEach((trigger) => {
      allVars.push(...getNodeVarsForHandle(node, trigger))
    })
  } else {
    // Get node vars for each output handle
    outputStates?.forEach((state) => {
      allVars.push(...getNodeVarsForHandle(node, state.id))
    })
  }

  // Remove duplicates
  return [...new Set(allVars)]
}

// Feature: Nodevars
export const getNodeVarsForHandle = (node?: Node<NodeDataBase<unknown>>, handleName?: string) => {
  const nodeName = node?.data.config.name
  const type = node?.type as FlowNodeTypes
  if (!nodeName || !type) return []

  const meta = {
    httpResp:
      'http_request' in (node?.data.config || {})
        ? (node?.data.config as { http_request?: { response_format: string } }).http_request
            ?.response_format
        : null,
    aiAgent:
      'actions' in (node?.data.config || {})
        ? (node?.data.config as unknown as { actions: Action[] }).actions?.map?.(
            (action) => action.variable_name ?? '', // TODO: debug to check getting empty variables
          ) || []
        : [],
    channel: (node?.data.config as { channel?: string }).channel,
    aiAgentV2:
      'extract_variables' in (node?.data.config || {})
        ? (
            node?.data.config as unknown as {
              extract_variables:
                | {
                    variable_name: string
                  }[]
                | null
            }
          ).extract_variables?.map?.((action) => action.variable_name ?? '') || []
        : [],
    agentNode:
      'agent_tasks' in (node?.data.config || {})
        ? (() => {
            const config = node?.data.config as unknown as {
              agent_tasks: {
                variables?: { name: string }[]
                extract_only?: { name: string }[]
              } | null
              actions?: {
                action_type?: string | null
                action_name?: string | null
                code_name?: string | null
                http_tool_name?: string | null
              }[]
            }
            const tasks = config.agent_tasks
            // An action's result persists as a node var keyed by its name
            // (livekit stores the response under action_name/code_name/
            // http_tool_name), so downstream nodes can reference it.
            const actionNames = (config.actions ?? []).map(
              (action) => action.action_name || action.code_name || action.http_tool_name || '',
            )
            return [...(tasks?.variables ?? []), ...(tasks?.extract_only ?? [])]
              .map((row) => row.name)
              .concat(actionNames)
              .filter(Boolean)
          })()
        : [],
  }

  const defaultNodeVars = getDefaultNodeVars({ meta })
  const defaultNodevars = defaultNodeVars[type]?.default || []
  const handleNodeVars = defaultNodeVars[type]?.[handleName || ''] || []

  // Add static node vars for nodes with preset message
  // This is a generic approach that works for any node type with a conversation_type field
  const hasPresetMessageConfig = defaultNodeVars[type]?.preset_message !== undefined
  const hasPresetMessageType =
    type === FlowNodeTypes.agent_preset_conversation ||
    ('conversation_type' in (node?.data.config || {}) &&
      (node?.data.config as { conversation_type?: string }).conversation_type === 'preset_message')

  // Check if wait_for_response is true
  const waitForResponse =
    'wait_for_response' in (node?.data.config || {}) &&
    Boolean((node?.data.config as { wait_for_response?: boolean }).wait_for_response)

  // Get preset message node vars from config if applicable
  const presetMessageVars =
    hasPresetMessageConfig && hasPresetMessageType
      ? defaultNodeVars[type]?.preset_message || []
      : []

  // Get additional vars for preset message with response if applicable
  const presetMessageWithResponseVars =
    hasPresetMessageConfig && hasPresetMessageType && waitForResponse
      ? defaultNodeVars[type]?.preset_message_with_response || []
      : []

  //* For api-request node, add the params as node vars
  let payloadFormat = {}
  if (node?.data.config?.payload_format) {
    if (typeof node.data.config.payload_format === 'string') {
      payloadFormat = JSON.parse(node.data.config.payload_format)
    } else {
      payloadFormat = node.data.config.payload_format
    }
  }
  const addApiRequestParams =
    nodeName === 'Start' &&
    typeof payloadFormat === 'object' &&
    Object.keys(payloadFormat || {}).length > 0 &&
    handleName === 'http'

  const nodeVars = [
    ...formatNodeVars(nodeName, defaultNodevars), // Add default node vars
    ...formatNodeVars(nodeName, handleNodeVars, handleName), // Add handle specific node vars
    ...formatNodeVars(nodeName, presetMessageVars), // Add preset message vars for nodes with preset_message type
    ...formatNodeVars(nodeName, presetMessageWithResponseVars), // Add additional vars for nodes with wait_for_response=true
    ...(addApiRequestParams
      ? formatNodeVars(
          nodeName,
          getPayloadPaths(payloadFormat).map(unescapeDotsInKey),
          `${handleName}.${handleNodeVars[0]}`,
        )
      : []), // Add http trigger node vars with nested paths
  ]
  return nodeVars
}

// Feature: AI Conversation Node Variable Descriptions
// This function specifically gets descriptions for AI conversation node variables
// while maintaining backward compatibility with existing getNodeVarsForHandle
export const getAiConversationNodeVarDescriptions = (node?: Node<NodeDataBase<unknown>>) => {
  if (!node || !isAiAgentV2Type(node.type)) return []

  const extractVariables = (
    node?.data.config as {
      extract_variables?: Array<{
        variable_name: string
        variable_instructions: string
      }>
    }
  )?.extract_variables

  if (!extractVariables || !Array.isArray(extractVariables)) return []

  return extractVariables
    .filter((variable) => variable.variable_name && variable.variable_instructions)
    .map((variable) => ({
      description: variable.variable_instructions,
      variableName: variable.variable_name,
      nodevar: `{{${node.data.config.name}.${variable.variable_name}}}`,
    }))
}

// Feature: Nodevars
export const refreshNodeVars = (nodeId: string) => {
  const instance = useFlowStore.getState().flowInstance
  if (!instance) return

  instance.setEdges((edges) =>
    produce(edges, (draft) => {
      draft.forEach((edge) => {
        if (edge.source === nodeId) {
          const node = instance.getNode(edge.source)
          if (!edge.data) edge.data = { nodeVars: [] }
          edge.data.nodeVars = getNodeVarsForHandle(node, edge.sourceHandle || '')
        }
      })
    }),
  )
}

// The subaccount tag rides agentSettings, so "tag picked but not saved yet" is
// just the staged-vs-initial compare below. Exported so the Start node's copy
// control can gate on the SAME notion of dirty instead of inventing a second one.
export const isSubaccountTagUnsaved = (
  agentSettings: AgentSettingsFormValues | null,
  initialAgentSettings: AgentSettingsFormValues | null,
) =>
  !!agentSettings &&
  !!initialAgentSettings &&
  agentSettings.sub_auth_id !== initialAgentSettings.sub_auth_id

// Core logic for detecting unsaved changes - shared between hook and utility function
export const compareFlowChanges = (
  currentNodes: Node<NodeDataBase<unknown>>[],
  currentEdges: Edge[],
  currentFlowType: PhloType,
  originalFlowData: FlowData | null,
  flowId?: string,
  // When true, transform errors propagate instead of being swallowed as
  // "no changes". The playground clean-check uses this so any doubt means save.
  options?: { rethrowOnError?: boolean },
) => {
  const {
    systemPrompt,
    sttGuidance,
    agentName,
    agentPersona,
    agentSettings,
    initialAgentSettings,
  } = useFlowStore.getState()
  // For new flows, check if there are any nodes or edges or system prompt changes
  if (!flowId) {
    const hasSystemPromptChanges =
      systemPrompt && (systemPrompt.prompt.trim() !== '' || systemPrompt.ai_nodes.length > 0)
    return currentNodes.length > 0 || currentEdges.length > 0 || !!hasSystemPromptChanges
  }

  // For existing flows, we need original data to compare
  if (!originalFlowData) {
    return false
  }

  try {
    // Transform current state to backend format for comparison
    const { data: currentTransformed } = transformFlowData(
      {
        nodes: currentNodes,
        edges: currentEdges,
        global_meta: {
          system_prompt: systemPrompt,
          stt_guidance: sttGuidance,
          agent_name: agentName,
          agent_persona: agentPersona,
        },
      },
      currentFlowType as FlowTypes,
      false,
    )

    // Transform original data to backend format for comparison
    const { data: originalTransformed } = transformFlowData(
      {
        nodes: originalFlowData.nodes,
        edges: originalFlowData.edges,
        global_meta: originalFlowData.global_meta ?? undefined,
      },
      currentFlowType as FlowTypes,
      false,
    )

    // Compare the transformed data structures
    const hasNodeChanges = !isEqual(currentTransformed.nodes, originalTransformed.nodes)
    const hasEdgeChanges = !isEqual(currentTransformed.connections, originalTransformed.connections)
    const hasNoteChanges = !isEqual(currentTransformed.notes, originalTransformed.notes)

    // Compare system prompt changes
    const hasSystemPromptChanges = !isEqual(
      currentTransformed.global_meta?.system_prompt,
      originalTransformed.global_meta?.system_prompt,
    )

    // Compare STT guidance changes
    const hasSttGuidanceChanges = !isEqual(
      currentTransformed.global_meta?.stt_guidance,
      originalTransformed.global_meta?.stt_guidance,
    )

    // Compare agent identity changes
    const hasAgentIdentityChanges =
      !isEqual(
        currentTransformed.global_meta?.agent_name,
        originalTransformed.global_meta?.agent_name,
      ) ||
      !isEqual(
        currentTransformed.global_meta?.agent_persona,
        originalTransformed.global_meta?.agent_persona,
      )

    // Compare agent settings changes (voice_ai_config, schedule, event_callbacks)
    // Skip knowledge_base_ids as those are managed separately
    const hasAgentSettingsChanges =
      !!agentSettings &&
      !!initialAgentSettings &&
      (!isEqual(agentSettings.voice_ai_config, initialAgentSettings.voice_ai_config) ||
        !isEqual(agentSettings.schedule, initialAgentSettings.schedule) ||
        !isEqual(agentSettings.event_callbacks, initialAgentSettings.event_callbacks) ||
        // The subaccount tag rides agentSettings now — a change lights Save.
        isSubaccountTagUnsaved(agentSettings, initialAgentSettings))

    return (
      hasNodeChanges ||
      hasEdgeChanges ||
      hasNoteChanges ||
      hasSystemPromptChanges ||
      hasSttGuidanceChanges ||
      hasAgentIdentityChanges ||
      hasAgentSettingsChanges
    )
  } catch (error) {
    if (options?.rethrowOnError) throw error
    // Handle MultipleNodeValidationError as debug log (not sent to Sentry)
    if (error instanceof MultipleNodeValidationError) {
      logger.debug('Flow data validation error while detecting unsaved changes:', error)
      return false
    }
    logger.warn('Error detecting unsaved changes:', error)
    return false
  }
}

// A utility function to detect unsaved changes by comparing current flow state with original backend data
export const detectUnsavedChanges = () => {
  const { flowInstance, currentFlowType, isEditing, tabFormInstances } = useFlowStore.getState()

  if (!isEditing || !flowInstance || !currentFlowType) {
    return false
  }

  // Check tab form instances for unsaved changes (voice config, settings, event callbacks)
  const hasTabFormChanges = Object.values(tabFormInstances).some((form) => {
    if (!form) return false
    const defaultValues =
      typeof form.options.defaultValues === 'function'
        ? form.options.defaultValues()
        : form.options.defaultValues
    return !isEqual(form.state.values, defaultValues)
  })
  if (hasTabFormChanges) return true

  // Get current flow state
  const currentNodes = flowInstance.getNodes()
  const currentEdges = flowInstance.getEdges()

  const id = getFlowIdFromUrl()

  // Get the original backend data from React Query cache (only for existing flows)
  const originalFlowData =
    id && id !== 'new'
      ? queryClient.getQueryData<FlowData>(flowQueryKeys().flowData(id)) || null
      : null

  return compareFlowChanges(currentNodes, currentEdges, currentFlowType, originalFlowData, id)
}

// A utility function to check if there are unsaved changes in the flow
// If there are unsaved changes, it will open the unsaved changes modal
// If there are no unsaved changes, it will call the callback function
// If there are unsaved changes, it will wait for the user to confirm the changes
// If the user confirms the changes, it will call the callback function
// If the user cancels the changes, it will not call the callback function
export const checkUnsavedChanges = (updateState = false, callback?: () => void) => {
  const { selectedNodeID } = useFlowStore.getState()
  const formInstance = useFlowStore.getState().sidebarFormInstance
  const formData = formInstance?.state.values
  const getNodeData = () => {
    if (selectedNodeID === SYSTEM_PROMPT_NODE_ID) {
      return useFlowStore.getState().systemPrompt
    }
    return useFlowStore
      .getState()
      .flowInstance?.getNodes()
      .find((node) => node.id === selectedNodeID)
  }

  const isSystemPrompt = (data?: GenericNode | SystemPrompt | null): data is SystemPrompt => {
    if (!data) return false
    return 'prompt' in data
  }

  const nodeData = getNodeData()

  const unsavedChanges = (() => {
    if (!selectedNodeID || !formData) return false
    if (isSystemPrompt(nodeData)) {
      const initialValues = formInstance?.options?.defaultValues
      return initialValues ? !isEqual(initialValues, formData) : false
    }
    if (!formInstance?.options?.defaultValues) return false

    return nodeData?.type
      ? !compareWithIgnoredFields(
          formInstance.options.defaultValues,
          formData,
          nodeData.type as FlowNodeTypes,
        )
      : false
  })()

  if (updateState) {
    useFlowStore.getState().setUnsavedModalOpen(unsavedChanges)
  }
  if (callback) {
    if (!unsavedChanges) callback()
    else {
      agentEventBus.once('SIDEBAR_FORM_UNSAVED_CHANGES_RESPONSE', (response) => {
        if (response) callback()
      })
    }
  }
  return unsavedChanges
}

// A utility function to check if there are unsaved changes in the flow for navigation
// If there are unsaved changes, it will open the flow navigation modal
// If there are no unsaved changes, it will call the callback function
// If there are unsaved changes, it will wait for the user to confirm the changes
// If the user confirms the changes, it will call the callback function
// If the user cancels the changes, it will not call the callback function
export const checkFlowUnsavedChangesForNavigation = (
  updateState = false,
  callback?: () => void,
) => {
  const { isEditing } = useFlowStore.getState()
  const hasFlowUnsavedChanges = Boolean(isEditing && detectUnsavedChanges())

  if (updateState) {
    useFlowStore.getState().setUnsavedModalOpen(hasFlowUnsavedChanges)
  }

  if (callback) {
    if (!hasFlowUnsavedChanges) {
      callback()
    } else {
      agentEventBus.once('UNSAVED_CHANGES_POPUP_RESPONSE', (response) => {
        if (response) callback()
      })
    }
  }

  return hasFlowUnsavedChanges
}

export const getOutputStateName = (
  type: FlowNodeTypes,
  config: NodeDataBase['config'],
  sourceHandle: string | undefined | null,
) => {
  if (type === FlowNodeTypes.start) {
    const flowType = useFlowStore.getState().currentFlowType
    if (flowType === 'omni' && sourceHandle) {
      return FlowSourceTypeToTriggerTitle[sourceHandle] || ''
    }
    return ''
  }
  const outputStates = getOutputStates({
    data: config,
    type: type as FlowNodeTypes,
  })
  const outputStateLabel = outputStates?.find((state) => state.id === sourceHandle)?.name
  return outputStateLabel
}

// Customer-facing hover copy for fixed-outcome ports (screening); user-authored
// intents have none.
export const getOutputStateDescription = (
  type: FlowNodeTypes,
  config: NodeDataBase['config'],
  sourceHandle: string | undefined | null,
) => {
  if (type === FlowNodeTypes.start) return undefined
  const outputStates = getOutputStates({
    data: config,
    type: type as FlowNodeTypes,
  })
  return outputStates?.find((state) => state.id === sourceHandle)?.description
}

// Feature: Nodevars
export const getNodeVarsFromAncestorEdges = (
  selectedNodeId: string,
  edges: Edge<EdgeData>[],
  visitedNodes = new Set(),
) => {
  if (!selectedNodeId) return []
  if (visitedNodes.has(selectedNodeId)) return []
  visitedNodes.add(selectedNodeId)

  const parentEdges = edges.filter((edge) => edge.target === selectedNodeId)

  let nodeVars: string[] = []
  parentEdges.forEach((edge) => {
    nodeVars = [...nodeVars, ...(edge?.data?.nodeVars || [])]
    nodeVars = [...nodeVars, ...getNodeVarsFromAncestorEdges(edge.source, edges, visitedNodes)]
  })

  return nodeVars
}

// Feature: Refresh Nodevars from Ancestors
// This function refreshes nodevars for all edges connected to the current node
// by recalculating nodevars from all ancestor nodes
export const refreshNodeVarsFromAncestors = (selectedNodeId: string) => {
  const instance = useFlowStore.getState().flowInstance
  if (!instance) return

  const { getNodes, getEdges, setEdges } = instance
  const nodes = getNodes()
  const edges = getEdges()

  // Get all edges that target the selected node
  const targetEdges = edges.filter((edge) => edge.target === selectedNodeId)

  // Refresh nodevars for each target edge
  setEdges((currentEdges) =>
    produce(currentEdges, (draft) => {
      targetEdges.forEach((targetEdge) => {
        const edgeInDraft = draft.find((e) => e.id === targetEdge.id)
        if (edgeInDraft) {
          // Recalculate nodevars for the source node of this edge
          const sourceNode = nodes.find((node) => node.id === targetEdge.source)
          if (sourceNode) {
            if (!edgeInDraft.data) edgeInDraft.data = { nodeVars: [] }
            edgeInDraft.data.nodeVars = getNodeVarsForHandle(
              sourceNode,
              targetEdge.sourceHandle || '',
            )
          }
        }
      })
    }),
  )
}

// To open the node in the sidebar
export const clickNode = (nodeId: string) => {
  setTimeout(() => {
    const el = document.querySelector(`[data-id="${nodeId}"] .flow-node`) as HTMLElement
    el?.click()
  }, 200)
}

// Function to get the next node name based on the existing nodes of the same type
export function getNextNodeName(nodes?: Node<NodeDataBase<unknown>>[], name?: string) {
  if (!name) return 'Node Name'
  // Regular expression to match the node name pattern and extract the number
  const regex = new RegExp(`^${name} (\\d+)$`, 'i')

  // Extract the max number from existing node names of the same type
  // Find the highest number suffix among existing node names
  // Example: For nodes ["Node", "Node 2", "Node 3"] returns 3
  const maxNumber =
    nodes
      ?.map((node) => {
        // For nodes without a number suffix (e.g. "Node"), treat as "Node 1"
        if (node.data.config?.name?.toLowerCase() === name.toLowerCase()) return 1
        // Extract number from node name (e.g. "Node 2" -> 2)
        const match = node.data.config?.name?.match(regex)
        // Return the number if found, otherwise 0
        return match ? parseInt(match[1], 10) : 0
      })
      // Get the highest number using reduce
      .reduce((max, num) => Math.max(max, num), 0) || 0
  // Generate the new node name
  return maxNumber > 0 ? `${name} ${maxNumber + 1}` : name
}

export const getDefaultNodeData = (
  type: FlowNodeTypes,
  getNodes: () => Node<NodeDataBase<unknown>>[],
) => {
  const data = cloneDeep(defaultNodeData[type]) // or else it will mutate the default data

  data.config.name = getNextNodeName(getNodes(), data.config.name)
  return data
}

// Node config keys that are AI-generated METADATA (outputs of generate-node-metadata),
// not node behavior. Changes to these must never themselves trigger a regeneration.
export const AI_METADATA_KEYS = ['name', 'description']

// Mirrors the constraints the rest of the stack enforces: aiassist generates names with
// this exact charset and a 30-char cap; config-service requires >=3 sanitized chars on
// publish; '.', '{' and '}' would break {{Node Name.variable}} reference resolution.
export const nodeNameSchema = z
  .string()
  .trim()
  .min(3, 'Name must be at least 3 characters')
  .max(30, 'Name must be at most 30 characters')
  .regex(/^[a-zA-Z0-9 _-]+$/, 'Use only letters, numbers, spaces, hyphens and underscores')

export const getDefaultNameConfig = () => ({
  name: 'name',
  componentType: 'input' as const,
  label: 'Name',
  zodValidation: nodeNameSchema,
  className: 'col-span-2',
  inputProps: {
    placeholder: 'Node name',
    maxLength: 30,
  },
  onBlurValidation: z.string().refine((value) => {
    const store = useFlowStore.getState()
    const selectedNodeId = store.selectedNodeID
    const nodes = store.flowInstance?.getNodes().filter((node) => node.id !== selectedNodeId)
    // Trimmed, case-insensitive comparison — the same definition of "unique" the
    // AI-name guard uses; an untrimmed or re-cased near-duplicate would otherwise
    // pass blur validation and surface as a backend error on save.
    const normalized = value.trim().toLowerCase()
    return !nodes?.some((node) => node.data.config.name.trim().toLowerCase() === normalized)
  }, 'Name must be unique'),
})

// Facing CORS error, need to find another way
export async function isValidAudioUrl(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD' })
    return !!(response.ok && response.headers.get('content-type')?.startsWith('audio/'))
  } catch (error) {
    logger.warn('Invalid audio URL', { error })
    return false
  }
}

export const updateCurrentFlowData = (
  updater: (draft: FlowData) => void,
  afterUpdate?: () => void,
) => {
  const id = getFlowIdFromUrl()
  if (!id) return

  // Explicitly type the data that setQueryData might return to its callback
  queryClient.setQueryData<FlowData | undefined>(flowQueryKeys().flowData(id), (oldData) => {
    // oldData can be FlowData or undefined
    if (!oldData) {
      // If there's no existing data, we cannot effectively merge a partial update.
      // Returning undefined means the cache won't be changed if the item doesn't exist.
      // Consider logging a warning if this scenario is unexpected:
      // console.warn(`Attempted to update non-existent flow data for id: ${id}`);
      return undefined
    }
    // Use produce to create a new state by applying changes to a draft
    return produce(oldData, (draft) => {
      updater(draft)
    })
  })
  queueMicrotask(() => {
    afterUpdate?.()
  })
}

export const flowFilterActive = (flow: RFlows) => flow.state === AgentState.ACTIVE
export const flowFilterDraft = (flow: RFlows) => flow.state === AgentState.DRAFT
export const flowFilterPaused = (flow: RFlows) => flow.state === AgentState.PAUSED

export const getProviders = () => {
  const language = useFlowStore.getState().agentSettings?.voice_ai_config?.language || 'en'
  const providers = queryClient.getQueryData<ApiResponse<RProviders>>(
    flowQueryKeys().providers(language),
  )
  return providers?.data
}

// Pre-compute the reverse mapping from trigger string to FlowType
export const TriggerStringToFlowTypeMap = Object.entries(FlowSourceTypes).reduce(
  (acc, [flowType, triggerValue]) => {
    acc[triggerValue] = flowType as FlowTypes
    return acc
  },
  {} as Record<string, FlowTypes>,
)

export const getPhloTypeFromStartNodeTrigger = (node: Node): FlowTypes | undefined => {
  // @ts-expect-error - this is a valid type
  const startNodeTriggerType = node.data.config.triggers[0] as TriggerType

  // Use the pre-computed map for a direct lookup
  return TriggerStringToFlowTypeMap[startNodeTriggerType]
}

export const getTriggerTypeFromPhloType = (flowType: FlowTypes): TriggerType | undefined => {
  const triggerString = FlowSourceTypes[flowType]
  return triggerString as TriggerType
}

export const getChannelTypeFromFlowType = (flowType: FlowTypes): AgentChannels => {
  switch (flowType) {
    case FlowTypes.incoming_call:
    case FlowTypes.outgoing_call:
      return AgentChannels.call
    case FlowTypes.incoming_sms:
    case FlowTypes.outgoing_sms:
      return AgentChannels.message
    case FlowTypes.incoming_chat:
      return AgentChannels.chat
    case FlowTypes.incoming_whatsapp:
    case FlowTypes.outgoing_whatsapp:
      return AgentChannels.whatsapp
    case FlowTypes.incoming_slack:
      return AgentChannels.slack
    default:
      return AgentChannels.call
  }
}

interface FlowInfo {
  name?: string
  id?: string
}

interface EventCallbackSchemaOptions {
  fieldPath: string
  flow?: FlowInfo
  allNodeVars?: string[]
  transcription?: boolean
}

export const generateEventCallbackSchema = (options: EventCallbackSchemaOptions) => {
  const { fieldPath, flow, allNodeVars = [], transcription = false } = options

  const hangupNodeVars = {
    is_agent_assigned: '',
    call_duration: '',
  }

  const eventName = fieldPath.split('.')[1]
  const isRecording = eventName === 'RECORDING'
  const isHangup = eventName === 'HANGUP'
  const isConversationClosure = eventName === 'CONVERSATION_CLOSURE'
  const isMessagingEvent = eventName === 'SMS_STATUS' || eventName === 'WHATSAPP_STATUS'

  const eventData: Record<string, unknown> = {}

  // Add recording-specific data
  if (isRecording) {
    eventData.recording_url = ''
    if (transcription) {
      eventData.transcription = ''
    }
  }

  // Add hangup-specific data
  if (isHangup) {
    Object.entries(hangupNodeVars).forEach(([key, value]) => {
      eventData[key] = value
    })
    // Add all node variables
    allNodeVars.forEach((nodeVar) => {
      // Remove the {{ }} braces for the key
      //  The value is the same as the key
      const cleanKey = nodeVar.replace(/^\{\{|\}\}$/g, '')
      eventData[cleanKey] = nodeVar
    })
  }

  // Add messaging-specific data
  if (isMessagingEvent) {
    eventData.channel = ''
    eventData.message_uuid = ''
    eventData.status = ''
    eventData.error_code = ''
  }

  // Add conversation closure-specific data
  if (isConversationClosure) {
    eventData.is_agent_assigned = ''
    eventData.channels = ''
    eventData.disposition = '(optional)'
    eventData.wrapup_note = '(optional)'
    eventData.chunks = [
      {
        from: '',
        to: '',
        type: '',
        message: '',
        channel: '',
        timestamp: '',
        attributes: '(optional)',
      },
    ]
    // Add all node variables
    allNodeVars.forEach((nodeVar) => {
      const cleanKey = nodeVar.replace(/^\{\{|\}\}$/g, '')
      eventData[cleanKey] = nodeVar
    })
  }

  const schemaObject: Record<string, unknown> = {
    created_at: '<timestamp for event>',
    data: {
      object: {
        ...(!(isConversationClosure || isMessagingEvent) && { call_uuid: '' }),
        ...(isMessagingEvent && { from: '', to: '' }),
        conversation_id: '',
        conversation_url: '',
        event_data: eventData,
        event_name: eventName.toLowerCase(),
        flow_name: flow?.name || '',
        flow_run_uuid: '',
        flow_uuid: flow?.id || '',
        node_name: '',
      },
    },
    id: '<unique identifier for the event>',
  }

  return JSON.stringify(schemaObject, null, 2)
}

export const getAppDetails = (
  appName: string,
  externalAppsResponse?: RExternalApp,
): RExternalAppDetails | null => {
  if (!externalAppsResponse || appName === 'Custom Secrets') return null

  // Search through all categories in external apps response
  const allApps = Object.values(externalAppsResponse).flat()

  const matchingApp = allApps.find((app) => app.provider.toLowerCase() === appName.toLowerCase())

  return matchingApp || null
}

// Feature: Nodevars - Get nodes that come after a given node (downstream)
export const getDownstreamNodes = (
  sourceNodeId: string,
  edges: Edge<EdgeData>[],
  visitedNodes = new Set(),
): string[] => {
  if (!sourceNodeId) return []
  if (visitedNodes.has(sourceNodeId)) return []
  visitedNodes.add(sourceNodeId)

  const downstreamEdges = edges.filter((edge) => edge.source === sourceNodeId)

  let downstreamNodeIds: string[] = []
  downstreamEdges.forEach((edge) => {
    downstreamNodeIds.push(edge.target)
    downstreamNodeIds = [
      ...downstreamNodeIds,
      ...getDownstreamNodes(edge.target, edges, visitedNodes),
    ]
  })

  return [...new Set(downstreamNodeIds)]
}

// Position a new child node below its source, or to the right of the source's
// existing children so siblings don't overlap. Shared by the add-node menu and
// the call-trigger prompt.
export const getChildNodePosition = (
  sourceNode: Node<NodeDataBase<unknown>>,
  nodes: Node<NodeDataBase<unknown>>[],
  edges: Edge[],
) => {
  const outGoers = getOutgoers({ id: sourceNode.id }, nodes, edges)
  const { position } = sourceNode
  if (!outGoers.length) return { x: position.x, y: position.y + 250 }
  const nodeWithMaxX = maxBy(outGoers, (node) => node.position.x)
  return {
    x: (nodeWithMaxX?.position.x ?? position.x) + 300,
    y: nodeWithMaxX?.position.y ?? position.y,
  }
}

// Feature: Audio Preview - Create voice preview handler with dependencies
export const createVoicePreviewHandler =
  (
    play: (voiceId: string, url: string) => Promise<void>,
    textToSpeechMutation: ReturnType<typeof useTextToSpeechMutation>,
  ) =>
  async (params: VoicePreviewParams) => {
    try {
      if (params.previewUrl) {
        await play(params.voiceId, params.previewUrl)
      } else {
        throw new Error('No preview URL')
      }
    } catch (_error) {
      try {
        if (!params.model || !params.provider) return

        const result = await textToSpeechMutation.mutateAsync({
          text: "Hello, I'm your AI assistant. How can I help you today?",
          voice_id: params.voiceId,
          model_id: params.model,
          provider_id: params.provider,
          language_code: params.languageCode,
        })
        await play(params.voiceId, result.pronunciation_preview_url)
      } catch {
        // Handle error silently
      }
    }
  }

// Renaming a node must rewrite {{oldName.var}} references in downstream nodes,
// deferred a microtask so edge state is fresh. Single entry point shared by the
// AI-metadata path and manual (pinned) renames — keep the two paths identical.
export const scheduleNodeVariableReferenceRewrite = (
  changedNodeId: string,
  oldName: string | undefined,
  newName: string | undefined,
  updateNodeData: (
    nodeId: string,
    updater: (oldNode: Node<NodeDataBase<unknown>>) => Partial<NodeDataBase<unknown>>,
  ) => void,
  getEdges: () => Edge<EdgeData>[],
) => {
  if (!oldName || !newName || oldName === newName) return
  queueMicrotask(() => {
    updateNodeVariableReferences(changedNodeId, oldName, newName, updateNodeData, getEdges)
  })
}

// Feature: Nodevars - Update node variable references when node names change
export const updateNodeVariableReferences = (
  changedNodeId: string,
  oldName: string,
  newName: string,
  updateNodeData: (
    nodeId: string,
    updater: (oldNode: Node<NodeDataBase<unknown>>) => Partial<NodeDataBase<unknown>>,
  ) => void,
  getEdges: () => Edge<EdgeData>[],
) => {
  if (!oldName || !newName || oldName === newName) return

  // Get fresh edges at execution time to avoid race conditions
  const edges = getEdges()
  const downstreamNodeIds = getDownstreamNodes(changedNodeId, edges)

  if (downstreamNodeIds.length === 0) return

  // Helper function to recursively update string values in an object
  const updateStringValues = (obj: unknown): unknown => {
    if (typeof obj === 'string') {
      // Replace patterns like {{OldName.anything}} with {{NewName.anything}}
      const regex = new RegExp(
        `\\{\\{${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\.\\w+)*\\}\\}`,
        'g',
      )
      return obj.replace(regex, (match) => match.replace(oldName, newName))
    }

    if (Array.isArray(obj)) {
      return obj.map(updateStringValues)
    }

    if (obj && typeof obj === 'object') {
      const updated: Record<string, unknown> = {}
      // Use Object.keys instead of Object.entries to avoid for...of
      Object.keys(obj).forEach((key) => {
        updated[key] = updateStringValues((obj as Record<string, unknown>)[key])
      })
      return updated
    }

    return obj
  }

  // Update each downstream node
  downstreamNodeIds.forEach((nodeId) => {
    updateNodeData(nodeId, (oldNode: Node<NodeDataBase<unknown>>) => {
      // Get fresh node data to avoid race conditions
      const currentConfig = oldNode.data.config
      const updatedConfig = updateStringValues(currentConfig)

      // Only update if something actually changed
      if (!isEqual(currentConfig, updatedConfig)) {
        return {
          config: updatedConfig as NodeDataBase<unknown>['config'],
        }
      }

      // Return unchanged if no updates needed
      return {}
    })
  })
}

export const getFlowIdFromUrl = () => {
  const path = window.location.pathname
  // Handle potential subpath deployments by finding the builder path
  const prefix = `${AGENT_BUILDER_BASE_PATH}/`
  const builderIndex = path.indexOf(prefix)
  if (builderIndex === -1) return 'new'

  const afterBuilder = path.substring(builderIndex + prefix.length)
  const id = afterBuilder.split('/')[0] // Get first segment after the builder base path
  return id || 'new'
}

// Simple function to update nodes with channel changes
export const updateSimilarNodesWithTriggerChange = (
  oldTriggerType: FlowTypes,
  newTriggerType: FlowTypes,
  getNodes: () => Node<NodeDataBase<unknown>>[],
  updateNodeData: (
    nodeId: string,
    updater: (oldNode: Node<NodeDataBase<unknown>>) => Partial<NodeDataBase<unknown>>,
  ) => void,
) => {
  // Skip API request changes
  if (oldTriggerType === FlowTypes.api_request || newTriggerType === FlowTypes.api_request) {
    return
  }

  const oldChannel = getChannelTypeFromFlowType(oldTriggerType)
  const newChannel = getChannelTypeFromFlowType(newTriggerType)

  if (!oldChannel || oldChannel === newChannel) return

  // Update all nodes with matching channel
  getNodes().forEach((node) => {
    if (!NODE_TYPES_WITH_CHANNEL.includes(node.type as FlowNodeTypes)) return

    updateNodeData(node.id, (oldNode) => {
      const config = oldNode.data.config as Record<string, unknown>
      if (config.channel === oldChannel) {
        return {
          config: {
            ...config,
            channel: newChannel,
          } as unknown as NodeDataBase<unknown>['config'],
        }
      }
      return {}
    })
  })
}

// Simple function to update similar nodes when user changes channel in sidebar
export const updateSimilarNodesWithSameChannel = (
  updatedNodeId: string,
  oldChannel: AgentChannels,
  newChannel: AgentChannels,
  getNodes: () => Node<NodeDataBase<unknown>>[],
  updateNodeData: (
    nodeId: string,
    updater: (oldNode: Node<NodeDataBase<unknown>>) => Partial<NodeDataBase<unknown>>,
  ) => void,
) => {
  if (!oldChannel || oldChannel === newChannel) return

  const updatedNode = getNodes().find((node) => node.id === updatedNodeId)
  if (!updatedNode?.type || !NODE_TYPES_WITH_CHANNEL.includes(updatedNode.type as FlowNodeTypes))
    return

  // Update nodes of same type with matching channel
  getNodes().forEach((node) => {
    if (node.id === updatedNodeId || node.type !== updatedNode.type) return

    updateNodeData(node.id, (oldNode) => {
      const config = oldNode.data.config as Record<string, unknown>
      if (config.channel === oldChannel) {
        return {
          config: {
            ...config,
            channel: newChannel,
          } as unknown as NodeDataBase<unknown>['config'],
        }
      }
      return {}
    })
  })
}

/**
 * A flow is read-only only while it's a migrated legacy flow still owned by the
 * old console (backend sends is_editable=false). Duplicates of legacy flows are
 * is_legacy too but fully editable. A missing is_editable (older backend) keeps
 * legacy flows read-only, matching the previous behavior.
 */
export const isLegacyReadOnly = (flow?: { is_legacy?: boolean; is_editable?: boolean }) =>
  !!flow?.is_legacy && flow?.is_editable !== true

/** Imported from the old console, not a duplicate of one: both are `is_legacy`,
 * only duplicates carry `duplicated_from`. */
export const isMigratedLegacyFlow = (flow?: { is_legacy?: boolean; duplicated_from?: string }) =>
  !!flow?.is_legacy && !flow?.duplicated_from
